import React, { Fragment, useEffect, useRef, useState } from 'react';

import {
    AUTH_LOG_PREFIX,
    AuthenticationConstants,
    POPUP_MESSAGE_TYPE,
    getCloudEnv,
    getOAuthClientId
} from '../../utilities/authConstants';

/* SDK provided by the CDN script in public/index.html. */
const getPlatformClient = () => {
    const w = window;
    return w.require && w.require('platformClient');
};

/**
 * Popup-window OAuth handler.
 *
 * Two phases run inside the same popup window:
 *  - Phase 1: `?code` absent  -> call loginPKCEGrant which redirects the popup
 *    to Genesys login.
 *  - Phase 2: `?code` present (after Genesys redirects back) -> exchange the
 *    code for an access token, postMessage it to the opener, close.
 *
 * `redirectUri` MUST be `${origin}/popuplogin` (NOT `${origin}/` or
 * `${origin}/oauth/callback`) so the popup re-mounts itself in phase 2 with
 * a `?code=...` it can exchange. Pointing the popup's redirect URI at a
 * route that does not re-mount this component will hang the flow.
 */
const PopupLogin = () => {
    const ranRef = useRef(false);
    const [status, setStatus] = useState('Authenticating...');

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        const postErrorAndClose = (message) => {
            setStatus(message);
            try {
                if (window.opener) {
                    window.opener.postMessage(
                        { type: POPUP_MESSAGE_TYPE, error: message },
                        window.location.origin
                    );
                }
            } catch (e) { /* noop */ }
            try { window.close(); } catch (e) { /* noop */ }
        };

        const redirectUri = `${window.location.protocol}//${window.location.host}/popuplogin`;
        const clientId = getOAuthClientId();
        const env = getCloudEnv();

        if (!window.opener) {
            // Popup opened directly (refresh / bookmark) - nothing to do.
            setStatus('This window can be closed.');
            return;
        }

        if (!clientId || !env) {
            postErrorAndClose('Missing client configuration in popup.');
            return;
        }

        const platformClient = getPlatformClient();
        if (!platformClient || !platformClient.ApiClient) {
            postErrorAndClose('OAuth library failed to load in popup.');
            return;
        }
        const client = platformClient.ApiClient.instance;

        try {
            client.setEnvironment(env);
        } catch (envErr) {
            console.error(AUTH_LOG_PREFIX, 'PopupLogin: invalid env', envErr);
            postErrorAndClose(`Invalid environment "${env}".`);
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const oauthError = searchParams.get('error');
        const authCode = searchParams.get('code');

        if (oauthError) {
            postErrorAndClose(oauthError);
            return;
        }

        if (!authCode) {
            // Phase 1: no code yet -> kick off PKCE, the SDK will redirect
            // this popup window to Genesys.
            console.debug(AUTH_LOG_PREFIX, 'PopupLogin: phase 1, calling loginPKCEGrant');
            setStatus('Redirecting to Genesys Cloud...');
            client
                .loginPKCEGrant(clientId, redirectUri)
                .catch((err) => {
                    console.error(AUTH_LOG_PREFIX, 'PopupLogin: loginPKCEGrant failed', err);
                    postErrorAndClose('Could not start sign-in.');
                });
            return;
        }

        // Phase 2: returned from Genesys with a code; exchange it.
        console.debug(AUTH_LOG_PREFIX, 'PopupLogin: phase 2, exchanging code');
        setStatus('Completing sign-in...');

        let codeVerifier = null;
        try {
            codeVerifier = sessionStorage.getItem(AuthenticationConstants.CodeVerifier);
        } catch (storageErr) {
            console.error(AUTH_LOG_PREFIX, 'PopupLogin: storage unavailable', storageErr);
            postErrorAndClose('Browser storage is unavailable.');
            return;
        }
        if (!codeVerifier) {
            postErrorAndClose('Missing PKCE code verifier in popup.');
            return;
        }

        client
            .authorizePKCEGrant(clientId, codeVerifier, authCode, redirectUri)
            .then((tokenResponse) => {
                const accessToken = tokenResponse && tokenResponse.accessToken;
                if (!accessToken) {
                    postErrorAndClose('Token exchange returned no accessToken.');
                    return;
                }
                const expiryRaw = tokenResponse.tokenExpiryTime;
                const expireOn = typeof expiryRaw === 'number'
                    ? expiryRaw
                    : Date.parse(String(expiryRaw));

                // Clear the verifier from popup storage (single-use).
                try {
                    sessionStorage.removeItem(AuthenticationConstants.CodeVerifier);
                } catch (e) { /* noop */ }

                try {
                    // Strict origin: never post to '*'. The opener's listener
                    // also validates `event.origin === window.location.origin`,
                    // so this is a two-sided check.
                    window.opener.postMessage(
                        {
                            type: POPUP_MESSAGE_TYPE,
                            accessToken,
                            expireOn: Number.isFinite(expireOn) ? expireOn : null,
                            env
                        },
                        window.location.origin
                    );
                } catch (postErr) {
                    console.error(AUTH_LOG_PREFIX, 'PopupLogin: postMessage failed', postErr);
                }
                setStatus('Sign-in complete. You can close this window.');
                try { window.close(); } catch (e) { /* noop */ }
            })
            .catch((err) => {
                console.error(AUTH_LOG_PREFIX, 'PopupLogin: authorizePKCEGrant failed', err);
                postErrorAndClose('Token exchange failed.');
            });
    }, []);

    return (
        <Fragment>
            <div style={{ padding: 24, textAlign: 'center', fontFamily: 'sans-serif' }}>
                {status}
            </div>
        </Fragment>
    );
};

export default PopupLogin;
